import { Fragment } from "react";
import Link from "next/link";
import { KennzahlIcon } from "./icons";
import { ABSPRUNG_TEXT, wertText, type Kennzahl, type KennzahlTon } from "./typen";

/** Statusmittel des Blocks: Farbe der Delta-Zeile (grün/rot). Warnung und neutral hat der Block nicht. */
const DELTA_KLASSEN: Record<KennzahlTon, string> = {
  ok: "flex items-center gap-x-1 text-green-600",
  risiko: "flex items-center gap-x-1 text-red-600",
  warn: "flex items-center gap-x-1",
  neutral: "flex items-center gap-x-1",
};

/**
 * Kandidat A: Kennzahl-Kachel mit Symbol und Absprung-Link.
 * Kann nicht: Aufschlüsselung, Ton „warn“ (nur grün/rot), Ton ohne Text.
 * Delta-Platz: Trend-Text (mit Pfeil), sonst Zusatz.
 */
export function KennzahlKachelA({ kennzahlen }: { kennzahlen: Kennzahl[] }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kennzahlen.map((k) => {
        const text = wertText(k);
        const delta = k.trend?.text ?? k.zusatz;
        const ton = k.ton ?? "neutral";
        return (
          <Fragment key={k.label}>
            {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-kpi-cards/kpi-card-with-icon-and-link */}
            <div className="flex flex-col bg-card border border-card-line shadow-2xs rounded-xl">
              <div className="p-4 md:p-5 flex justify-between gap-x-3">
                <div>
                  <p className="text-xs uppercase text-muted-foreground-1">{k.label}</p>
                  <div className="mt-1 flex items-center gap-x-2">
                    <h3 className="text-xl sm:text-2xl font-medium text-foreground">
                      {text === null ? <span role="img" aria-label="nicht verfügbar">—</span> : text}
                    </h3>
                    {delta && (
                      <span className={DELTA_KLASSEN[ton]}>
                        {k.trend?.richtung === "hoch" && (
                          <svg className="inline-block size-5 self-center" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                            <polyline points="16 7 22 7 22 13" />
                          </svg>
                        )}
                        {k.trend?.richtung === "runter" && (
                          <svg className="inline-block size-4 self-center" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                            <polyline points="16 17 22 17 22 11" />
                          </svg>
                        )}
                        <span className="inline-block text-lg">{delta}</span>
                      </span>
                    )}
                  </div>
                </div>
                {k.icon && (
                  <div className="shrink-0 flex justify-center items-center size-11 bg-primary text-primary-foreground rounded-full">
                    <KennzahlIcon name={k.icon} className="shrink-0 size-5" />
                  </div>
                )}
              </div>

              {k.href && (
                <Link
                  className="py-3 px-4 md:px-5 inline-flex justify-between items-center text-sm text-muted-foreground-2 border-t border-card-footer hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus rounded-b-xl"
                  href={k.href}
                >
                  <span>
                    {ABSPRUNG_TEXT}
                    <span className="sr-only">: {k.label}</span>
                  </span>
                  <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </Link>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
